import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { State } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridPageChangeEvent } from '@progress/kendo-react-grid'
import { TransactionValidationsDto } from 'api/Wallet/Types/TransactionValidations'
import { Modal } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import { TransactionValidationsColumns } from './TransactionValidationsColumns'

export enum TransactionValidationType {
  Yes = 1,
  No = 2,
}

interface TransactionValidationsProps {
  transactionId: string
  type: TransactionValidationType
}

const createDataState = (state: State, data: TransactionValidationsDto[]) => ({
  result: data
    .slice(state.skip, (state.take as number) + (state.skip as number))
    .sort((a, b) => stringComparator(a.name, b.name)),
  dataState: state,
})

const TransactionValidations: React.FC<TransactionValidationsProps> = ({ transactionId, type }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [dataState, setDataState] = useState<{
    result: TransactionValidationsDto[]
    dataState: State
  }>(createDataState(getPaginationInitialState(), []))

  const { callback: fetchData } = useCallbackWithForceRefresh(
    () =>
      isModalOpen
        ? walletsApiClient.getTransactionValidations(transactionId)
        : Promise.resolve(undefined),
    [transactionId, isModalOpen]
  )
  const { data: transactionValidations, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    setDataState(createDataState(dataState.dataState, transactionValidations || []))
  }, [transactionValidations, dataState.dataState])

  const pageChange = (event: GridPageChangeEvent) => {
    if (!transactionValidations) return
    setDataState(
      createDataState(
        {
          take: event.page.take,
          skip: event.page.skip,
        },
        transactionValidations
      )
    )
  }

  return (
    <>
      {isModalOpen && (
        <Modal
          title='Bulk Processing Information'
          visible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <Loading isLoading={isLoading}>
            <Grid
              onPageChange={pageChange}
              take={dataState.dataState.take}
              skip={dataState.dataState.skip}
              total={transactionValidations?.length}
              pageSize={dataState.dataState.take}
              data={dataState.result}
              resizable
              pageable={pageableSettings}
            >
              {TransactionValidationsColumns.filter((c) => c.show !== false).map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            </Grid>
          </Loading>
        </Modal>
      )}
      <Badge
        onClick={() => setIsModalOpen(true)}
        className='has-pointer'
        variant={type === TransactionValidationType.Yes ? 'green' : 'red'}
      >
        {type === TransactionValidationType.Yes ? 'Yes' : 'No'}
      </Badge>
    </>
  )
}

export default TransactionValidations
